import React from 'react';
import { motion } from 'framer-motion';
import { Check, ShieldCheck, Users, Lock, Moon, Sun, Clock, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';

export function TrustSection() {
  return (
    <section className="py-16 bg-gradient-to-b from-sky-50 to-white border-t border-gray-100">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Sleep Windows Visualization - New Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-16 rounded-xl overflow-hidden shadow-xl border border-sky-200 bg-gradient-to-r from-[#4CCEFD]/95 to-[#308CFD]/95"
        >
          <div className="p-8 bg-sky-50/10 backdrop-blur-sm">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-white mb-2">Sleep Pattern Visualization</h3>
              <p className="text-sky-100">Track and visualize your child's sleep patterns with precision</p>
            </div>
            
            <div className="flex flex-col md:flex-row gap-8 items-center">
              {/* Sleep Windows Chart */}
              <div className="flex-1 rounded-lg bg-gray-800/50 p-6 border border-indigo-500/20">
                <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <Clock className="mr-2 text-indigo-400" size={18} />
                  Sleep Windows
                </h4>
                
                <div className="relative h-64">
                  {/* Grid lines */}
                  <div className="absolute inset-0 grid grid-cols-8 gap-0">
                    {[...Array(8)].map((_, i) => (
                      <div key={i} className="border-r border-gray-700/50 h-full" />
                    ))}
                  </div>
                  <div className="absolute inset-0 grid grid-rows-4 gap-0">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="border-b border-gray-700/50 w-full" />
                    ))}
                  </div>
                  
                  {/* Time indicators */}
                  <div className="absolute bottom-0 w-full flex justify-between text-xs text-gray-400 px-2">
                    <span>18:00</span>
                    <span>20:00</span>
                    <span>22:00</span>
                    <span>00:00</span>
                    <span>02:00</span>
                    <span>04:00</span>
                    <span>06:00</span>
                    <span>08:00</span>
                  </div>
                  
                  {/* Sleep bars - Day 1 */}
                  <motion.div 
                    initial={{ width: 0 }}
                    whileInView={{ width: '45%' }}
                    transition={{ duration: 1, delay: 0.3 }}
                    className="absolute left-[15%] top-[20%] h-4 bg-gradient-to-r from-indigo-600 to-purple-500 rounded-full"
                  >
                    <div className="absolute -left-3 -top-1 w-6 h-6 bg-indigo-400 rounded-full flex items-center justify-center">
                      <Moon size={12} className="text-indigo-950" />
                    </div>
                    <div className="absolute -right-3 -top-1 w-6 h-6 bg-purple-400 rounded-full flex items-center justify-center">
                      <Sun size={12} className="text-indigo-950" />
                    </div>
                  </motion.div>
                  
                  {/* Sleep bars - Day 2 */}
                  <motion.div 
                    initial={{ width: 0 }}
                    whileInView={{ width: '40%' }}
                    transition={{ duration: 1, delay: 0.5 }}
                    className="absolute left-[18%] top-[40%] h-4 bg-gradient-to-r from-indigo-600 to-purple-500 rounded-full"
                  >
                    <div className="absolute -left-3 -top-1 w-6 h-6 bg-indigo-400 rounded-full flex items-center justify-center">
                      <Moon size={12} className="text-indigo-950" />
                    </div>
                    <div className="absolute -right-3 -top-1 w-6 h-6 bg-purple-400 rounded-full flex items-center justify-center">
                      <Sun size={12} className="text-indigo-950" />
                    </div>
                  </motion.div>
                  
                  {/* Sleep bars - Day 3 */}
                  <motion.div 
                    initial={{ width: 0 }}
                    whileInView={{ width: '50%' }}
                    transition={{ duration: 1, delay: 0.7 }}
                    className="absolute left-[12%] top-[60%] h-4 bg-gradient-to-r from-indigo-600 to-purple-500 rounded-full"
                  >
                    <div className="absolute -left-3 -top-1 w-6 h-6 bg-indigo-400 rounded-full flex items-center justify-center">
                      <Moon size={12} className="text-indigo-950" />
                    </div>
                    <div className="absolute -right-3 -top-1 w-6 h-6 bg-purple-400 rounded-full flex items-center justify-center">
                      <Sun size={12} className="text-indigo-950" />
                    </div>
                  </motion.div>
                  
                  {/* Sleep bars - Day 4 */}
                  <motion.div 
                    initial={{ width: 0 }}
                    whileInView={{ width: '55%' }}
                    transition={{ duration: 1, delay: 0.9 }}
                    className="absolute left-[10%] top-[80%] h-4 bg-gradient-to-r from-indigo-600 to-purple-500 rounded-full"
                  >
                    <div className="absolute -left-3 -top-1 w-6 h-6 bg-indigo-400 rounded-full flex items-center justify-center">
                      <Moon size={12} className="text-indigo-950" />
                    </div>
                    <div className="absolute -right-3 -top-1 w-6 h-6 bg-purple-400 rounded-full flex items-center justify-center">
                      <Sun size={12} className="text-indigo-950" />
                    </div>
                  </motion.div>
                </div>
                
                <div className="flex justify-between items-center mt-8">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-indigo-500 rounded-full mr-2"></div>
                    <span className="text-xs text-gray-300">Bedtime</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>
                    <span className="text-xs text-gray-300">Wake time</span>
                  </div>
                  <div className="flex items-center">
                    <Zap size={12} className="text-yellow-400 mr-1" />
                    <span className="text-xs text-gray-300">Sleep quality</span>
                  </div>
                </div>
              </div>
              
              {/* Sleep Duration Chart */}
              <div className="flex-1 rounded-lg bg-gray-800/50 p-6 border border-indigo-500/20">
                <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <Moon className="mr-2 text-indigo-400" size={18} />
                  Sleep Duration
                </h4>
                
                <div className="h-64 flex items-end justify-around">
                  {[6, 7.5, 9, 8, 7, 10, 8.5].map((hours, index) => {
                    const height = (hours / 12) * 100;
                    return (
                      <motion.div 
                        key={index}
                        initial={{ height: 0 }}
                        whileInView={{ height: `${height}%` }}
                        transition={{ duration: 0.8, delay: 0.2 + (index * 0.1) }}
                        className="w-8 bg-gradient-to-t from-indigo-600 to-purple-400 rounded-t-md relative group"
                      >
                        <div className="absolute -top-7 left-1/2 transform -translate-x-1/2 bg-indigo-800 px-2 py-1 rounded text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity">
                          {hours}h
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
                
                <div className="flex justify-between mt-4 text-xs text-gray-400 px-2">
                  <span>Mon</span>
                  <span>Tue</span>
                  <span>Wed</span>
                  <span>Thu</span>
                  <span>Fri</span>
                  <span>Sat</span>
                  <span>Sun</span>
                </div>
              </div>
            </div>
            
            <div className="mt-6 bg-indigo-900/50 p-4 rounded-lg border border-indigo-500/20">
              <p className="text-indigo-100 text-sm">
                SleepNest provides sleep coaches with powerful visualizations to understand your child's sleep patterns
                at a glance. Identify trends, track improvements, and make data-driven recommendations.
              </p>
            </div>
          </div>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {trustSignals.map((signal, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-lg p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-4">
                <div className="rounded-full bg-sky-100 p-2 flex-shrink-0">
                  <Check className="h-5 w-5 text-sky-600" />
                </div>
                <p className="text-gray-700">{signal}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="flex flex-col md:flex-row items-center justify-center gap-8 mb-12">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-2 px-5 py-3 bg-gray-50 rounded-full border border-gray-200"
          >
            <Lock className="h-4 w-4 text-gray-600" />
            <span className="text-sm text-gray-700 font-medium">SSL Secure Payments</span>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="flex items-center gap-2 px-5 py-3 bg-gray-50 rounded-full border border-gray-200"
          >
            <ShieldCheck className="h-4 w-4 text-gray-600" />
            <span className="text-sm text-gray-700 font-medium">Your Data Is Protected</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex items-center gap-2 px-5 py-3 bg-sky-50 rounded-full border border-sky-100"
          >
            <Users className="h-4 w-4 text-sky-600" />
            <span className="text-sm text-sky-700 font-medium">9 Pediatric Sleep Coaches Claimed Lifetime Offer Today</span>
          </motion.div>
        </div>
        
        {/* Trusted organization logos - only showing GentleBirth */}
        <div className="mt-12">
          <p className="text-center text-gray-500 text-sm uppercase tracking-wide mb-6">Trusted by leading organizations</p>
          <div className="flex justify-center items-center">
            <div className="h-10 text-lg font-bold text-gray-500">GentleBirth</div>
          </div>
        </div>

        {/* Privacy Policy Footer */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mt-6 text-sm text-gray-500"
        >
          <p className="mb-2">
            SleepNest adopts all privacy policies of Gentlebirth.
          </p>
          <div className="flex justify-center items-center space-x-4">
            <a 
              href="/privacy" 
              className="text-blue-600 hover:text-blue-800 transition-colors"
            >
              Privacy Policy
            </a>
            <span>•</span>
            <a 
              href="/terms" 
              className="text-blue-600 hover:text-blue-800 transition-colors"
            >
              Terms & Conditions
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

const trustSignals = [
  "Parents log baby sleep, feeding, and naps—pediatric sleep coaches see insights instantly.",
  "No manual spreadsheets or data headaches for pediatric sleep coaches—just fast answers.",
  "Cancel anytime, no commitment for pediatric sleep coaches."
];
